import React, { useEffect, useRef } from 'react';
import { Input } from 'reactstrap';
import CurrencyInput from 'react-currency-input-field';
import Helpers from '../../../../../../utils/helper';
import {
    PurchasePriceSummary,
    DeferredFixedPaymentRow,
    DeferredVariablePaymentRow,
    DeferredVariablePaymentType,
    NO_VALUE,
} from '../../../../../../slices/marketplace/loi-form/term-form';
import { TermFormFieldComment } from '../../../../../../slices/marketplace/loi-form/term-form-field-comment';
import { LoiTermFormFieldKeys } from '../../../../../../slices/marketplace/loi-form/loi-term-form-field-keys';
import { PriceSummaryTableRow } from './PriceSummaryTableRow';
import { EmployeeConsiderationPayer } from '../../../../../../slices/marketplace/loi-form/employee-consideration-payer.enum';

export interface TermsFormPriceSummaryBlockProps {
    summary: PurchasePriceSummary | null | undefined;
    total: number;
    onSummaryChange: (patch: Partial<PurchasePriceSummary>) => void;
    earnoutSum: number;
    syntheticJvSum: number;
    readOnly?: boolean;
    errors?: Set<string>;
    employeeConsiderationPayer?: EmployeeConsiderationPayer | null;
    isFsb?: boolean;
    isBuyerReview?: boolean;
    isBuyerReviewConfirmed?: boolean;
    fieldComments?: TermFormFieldComment[];
    onFieldCommentChange?: (fieldKey: string, comment: string | null) => void;
}

function formatPercent(val: number, total: number): string | null {
    if (total <= 0 || val <= 0) return null;
    return `${Math.round((val / total) * 1000) / 10}%`;
}

function computeNetToSeller(
    faceValue: number | null | undefined,
    consideration: number | null | undefined
): number | null {
    if (faceValue == null) return null;
    return faceValue - (consideration ?? 0);
}

const emptyDfpRow = (amount: number | null): DeferredFixedPaymentRow => ({
    paymentTiming: null,
    amount,
    annualizedInterestAccrualRate: null,
    contingent: NO_VALUE,
    contingencyDependentVariableValue: null,
    contDvMinimumValue: null,
    employmentContingency: NO_VALUE,
    tailPeriodForEmployment: null,
    considerationForEmployees: null,
    netToSeller: null,
    fairValue: null,
    fairValueExplanation: null,
});

const emptyDvpRow = (
    valueAtClosing: number | null,
    type?: DeferredVariablePaymentType
): DeferredVariablePaymentRow => ({
    paymentTiming: null,
    type: type ?? null,
    valueAtClosing,
    calculationDependentVariable: null,
    calcDvBaselineValue: null,
    calculationMultiple: null,
    maximumPayment: null,
    employmentContingency: NO_VALUE,
    tailPeriodForEmploymentContingency: null,
    penaltyMultiple: null,
    distributionPeriodicity: null,
    considerationForEmployees: null,
    netToSeller: null,
    fairValue: null,
    fairValueExplanation: null,
});

export const TermsFormPriceSummaryBlock = (
    props: TermsFormPriceSummaryBlockProps
) => {
    const {
        summary,
        total,
        onSummaryChange,
        earnoutSum,
        syntheticJvSum,
        readOnly = false,
        errors,
        employeeConsiderationPayer,
        isFsb,
        isBuyerReview,
        isBuyerReviewConfirmed,
        fieldComments,
        onFieldCommentChange,
    } = props;

    const getComment = (fieldKey: string) =>
        fieldComments?.find((c) => c.fieldKey === fieldKey)?.comment ?? null;

    const showComments = isBuyerReview || !!isBuyerReviewConfirmed;

    const commentInput = (fieldKey?: string) => {
        const comment = fieldKey ? getComment(fieldKey) : null;
        if (!fieldKey) return null;
        return (
            <Input
                type="textarea"
                value={comment ?? ''}
                readOnly={!isBuyerReview}
                onChange={(e) =>
                    onFieldCommentChange?.(
                        fieldKey,
                        e.target.value === '' ? null : e.target.value
                    )
                }
                className="terms-form-comment-input"
                aria-label={`Comment for ${fieldKey}`}
            />
        );
    };

    const commentCell = (fieldKey?: string) => {
        if (!showComments) return null;
        const comment = fieldKey ? getComment(fieldKey) : null;
        return (
            <td className="terms-form-col-comment">
                {fieldKey && (isBuyerReview || comment) && (
                    <Input
                        type="textarea"
                        value={comment ?? ''}
                        readOnly={!isBuyerReview}
                        onChange={(e) =>
                            onFieldCommentChange?.(
                                fieldKey,
                                e.target.value === '' ? null : e.target.value
                            )
                        }
                        className="terms-form-comment-input"
                        aria-label={`Comment for ${fieldKey}`}
                    />
                )}
            </td>
        );
    };

    const showEmployeeCols = employeeConsiderationPayer != null && employeeConsiderationPayer !== EmployeeConsiderationPayer.No;
    const showFairValueCols = isFsb === true;

    const cash = Number(summary?.cashAtCloser?.cashAtClose) || 0;
    const wchValue =
        Number(summary?.cashAtCloser?.workingCapitalHoldback?.value) || 0;
    const derivedNetCash =
        summary?.cashAtCloser?.cashAtClose != null ? cash - wchValue : null;
    const dfpRows = summary?.deferredFixedPayments?.rows ?? [];
    const dvpRows = summary?.deferredVariablePayments?.rows ?? [];
    const dfpTotal = dfpRows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
    const dvpTotal = dvpRows.reduce(
        (s, r) => s + (Number(r.valueAtClosing) || 0),
        0
    );
    const jvVal = Number(summary?.jvEquity?.row?.valueAtClosing) || 0;
    const corpVal = Number(summary?.corporateEquity?.row?.valueAtClosing) || 0;
    const other = Number(summary?.other) || 0;

    const totalFairValue = showFairValueCols
        ? (Number(summary?.cashAtCloser?.fairValue) || 0) +
          (Number(dfpRows[0]?.fairValue) || 0) +
          (Number(dvpRows[0]?.fairValue) || 0) +
          (Number(summary?.jvEquity?.row?.fairValue) || 0) +
          (Number(summary?.corporateEquity?.row?.fairValue) || 0) +
          (Number(summary?.otherFairValue) || 0)
        : 0;

    const latestRef = useRef({
        onSummaryChange,
        cashAtCloser: summary?.cashAtCloser,
    });
    useEffect(() => {
        latestRef.current = {
            onSummaryChange,
            cashAtCloser: summary?.cashAtCloser,
        };
    });

    useEffect(() => {
        const { onSummaryChange: fn, cashAtCloser } = latestRef.current;
        if (derivedNetCash !== (cashAtCloser?.netCashAtCloser ?? null)) {
            fn({
                cashAtCloser: {
                    ...cashAtCloser,
                    netCashAtCloser: derivedNetCash,
                },
            });
        }
    }, [derivedNetCash]);

    const rowProps = { showEmployeeCols, showFairValueCols, showComments, readOnly, errors };

    const patchDfpFirstRow = (patch: Partial<DeferredFixedPaymentRow>) =>
        onSummaryChange({
            deferredFixedPayments: {
                rows: dfpRows.length > 0
                    ? dfpRows.map((r, i) => i === 0 ? { ...r, ...patch } : r)
                    : [{ ...emptyDfpRow(null), ...patch }],
            },
        });

    const patchDvpFirstRow = (patch: Partial<DeferredVariablePaymentRow>) =>
        onSummaryChange({
            deferredVariablePayments: {
                rows: dvpRows.length > 0
                    ? dvpRows.map((r, i) => i === 0 ? { ...r, ...patch } : r)
                    : [{ ...emptyDvpRow(null, DeferredVariablePaymentType.Earnout), ...patch }],
            },
        });

    const wch = summary?.cashAtCloser?.workingCapitalHoldback;
    const wchHasValue = wchValue > 0;
    const wchLabel = (
        <div className="capital-holdback-line">
            <span>Working Capital Holdback</span>
            {wchHasValue && (
                <>
                    <Input
                        type="number"
                        value={wch?.monthsHold ?? ''}
                        disabled={readOnly}
                        onChange={(e) =>
                            onSummaryChange({
                                cashAtCloser: {
                                    ...summary?.cashAtCloser,
                                    workingCapitalHoldback: {
                                        ...wch,
                                        monthsHold: e.target.value === '' ? null : Number(e.target.value),
                                    },
                                },
                            })
                        }
                        invalid={errors?.has('workingCapitalHoldbackMonths')}
                        className="form-control form-control-sm terms-form-inline-months"
                        aria-label="Months Hold"
                    />
                    <span>Months Hold</span>
                </>
            )}
        </div>
    );

    return (
        <div className="terms-form-section-underlined terms-form-price-summary-block">
            <div className="terms-form-price-summary-table-container">
                <table className="terms-form-price-summary-table">
                    <thead>
                        <tr>
                            <th style={{width: '205px', minWidth:'205px'}} >
                                Purchase Price Consideration Summary, Face Value
                                at Closing
                            </th>
                            <th className="currency-column">Face Values</th>
                            <th className="percent-column">% of Total</th>
                            {showEmployeeCols && (
                                <>
                                    <th className="currency-column">Consideration for Employees</th>
                                    <th className="currency-column">Net to Seller</th>
                                </>
                            )}
                            {showFairValueCols && (
                                <>
                                    <th className="currency-column">Fair Values</th>
                                    <th className="terms-form-col-explanation">Explanation</th>
                                </>
                            )}
                            {showComments && (
                                <th className="terms-form-col-comment">Comments</th>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        <PriceSummaryTableRow
                            {...rowProps}
                            label="Cash at Close"
                            faceValue={summary?.cashAtCloser?.cashAtClose}
                            onFaceValueChange={(v) => onSummaryChange({ cashAtCloser: { ...summary?.cashAtCloser, cashAtClose: v } })}
                            faceValueAriaLabel="Cash at Close"
                            faceValueErrorKey="cashAtClose"
                            percent={formatPercent(cash, total)}
                            considerationForEmployees={summary?.cashAtCloser?.considerationForEmployees}
                            onConsiderationChange={(v) => onSummaryChange({ cashAtCloser: { ...summary?.cashAtCloser, considerationForEmployees: v, netToSeller: computeNetToSeller(summary?.cashAtCloser?.cashAtClose, v) } })}
                            considerationAriaLabel="Cash at Close - Consideration for Employees"
                            considerationErrorKey="cashAtCloserConsiderationForEmployees"
                            netToSeller={computeNetToSeller(summary?.cashAtCloser?.cashAtClose, summary?.cashAtCloser?.considerationForEmployees)}
                            netToSellerAriaLabel="Cash at Close - Net to Seller"
                            fairValue={summary?.cashAtCloser?.fairValue}
                            onFairValueChange={(v) => onSummaryChange({ cashAtCloser: { ...summary?.cashAtCloser, fairValue: v } })}
                            fairValueAriaLabel="Cash at Close - Fair Value"
                            fairValueErrorKey="cashAtCloserFairValue"
                            explanation={summary?.cashAtCloser?.fairValueExplanation}
                            onExplanationChange={(v) => onSummaryChange({ cashAtCloser: { ...summary?.cashAtCloser, fairValueExplanation: v } })}
                            explanationAriaLabel="Cash at Close - Fair Value Explanation"
                            commentNode={commentInput(LoiTermFormFieldKeys.cashAtClose)}
                        />

                        <PriceSummaryTableRow
                            {...rowProps}
                            label={wchLabel}
                            indented
                            faceValue={wch?.value}
                            onFaceValueChange={(v) => onSummaryChange({ cashAtCloser: { ...summary?.cashAtCloser, workingCapitalHoldback: { ...wch, value: v } } })}
                            faceValueAriaLabel="Working Capital Holdback"
                            faceValueErrorKey="workingCapitalHoldbackValue"
                            percent={null}
                            considerationForEmployees={wch?.considerationForEmployees}
                            onConsiderationChange={(v) => onSummaryChange({ cashAtCloser: { ...summary?.cashAtCloser, workingCapitalHoldback: { ...wch, considerationForEmployees: v, netToSeller: computeNetToSeller(wch?.value ? Number(wch.value) : null, v) } } })}
                            considerationAriaLabel="Working Capital Holdback - Consideration for Employees"
                            considerationErrorKey="workingCapitalHoldbackConsiderationForEmployees"
                            netToSeller={computeNetToSeller(wch?.value ? Number(wch.value) : null, wch?.considerationForEmployees)}
                            netToSellerAriaLabel="Working Capital Holdback - Net to Seller"
                            fairValue={wch?.fairValue}
                            onFairValueChange={(v) => onSummaryChange({ cashAtCloser: { ...summary?.cashAtCloser, workingCapitalHoldback: { ...wch, fairValue: v } } })}
                            fairValueAriaLabel="Working Capital Holdback - Fair Value"
                            fairValueErrorKey="workingCapitalHoldbackFairValue"
                            explanation={wch?.fairValueExplanation}
                            onExplanationChange={(v) => onSummaryChange({ cashAtCloser: { ...summary?.cashAtCloser, workingCapitalHoldback: { ...wch, fairValueExplanation: v } } })}
                            explanationAriaLabel="Working Capital Holdback - Fair Value Explanation"
                            commentNode={commentInput(LoiTermFormFieldKeys.workingCapitalHoldback)}
                        />

                        <PriceSummaryTableRow
                            {...rowProps}
                            label="Net Cash at Close"
                            faceValue={derivedNetCash}
                            allowNegativeFaceValue
                            faceValueAriaLabel="Cash at Close – Net Cash at Close"
                            commentNode={commentInput(LoiTermFormFieldKeys.netCashAtClose)}
                        />

                        <PriceSummaryTableRow
                            {...rowProps}
                            label="Deferred Fixed Payments"
                            faceValue={dfpRows.length && dfpRows[0]?.amount != null ? dfpRows[0].amount : null}
                            onFaceValueChange={(v) => onSummaryChange({ deferredFixedPayments: { rows: dfpRows.length > 0 ? dfpRows.map((r, i) => i === 0 ? { ...r, amount: v } : r) : [emptyDfpRow(v)] } })}
                            faceValueAriaLabel="Deferred Fixed Payments"
                            faceValueErrorKey="deferredFixedPayments"
                            percent={formatPercent(dfpTotal, total)}
                            considerationForEmployees={dfpRows[0]?.considerationForEmployees}
                            onConsiderationChange={(v) => patchDfpFirstRow({ considerationForEmployees: v, netToSeller: computeNetToSeller(dfpRows[0]?.amount, v) })}
                            considerationAriaLabel="Deferred Fixed Payments - Consideration for Employees"
                            considerationErrorKey="deferredFixedPaymentsConsiderationForEmployees"
                            netToSeller={computeNetToSeller(dfpRows[0]?.amount, dfpRows[0]?.considerationForEmployees)}
                            netToSellerAriaLabel="Deferred Fixed Payments - Net to Seller"
                            fairValue={dfpRows[0]?.fairValue}
                            onFairValueChange={(v) => patchDfpFirstRow({ fairValue: v })}
                            fairValueAriaLabel="Deferred Fixed Payments - Fair Value"
                            fairValueErrorKey="deferredFixedPaymentsFairValue"
                            explanation={dfpRows[0]?.fairValueExplanation}
                            onExplanationChange={(v) => patchDfpFirstRow({ fairValueExplanation: v })}
                            explanationAriaLabel="Deferred Fixed Payments - Fair Value Explanation"
                            commentNode={commentInput(LoiTermFormFieldKeys.deferredFixedPaymentsTotal)}
                        />

                        <PriceSummaryTableRow
                            {...rowProps}
                            label="Deferred Variable Payments"
                            faceValue={dvpTotal !== 0 ? dvpTotal : dvpRows[0]?.valueAtClosing ?? null}
                            onFaceValueChange={(v) => onSummaryChange({ deferredVariablePayments: { rows: dvpRows.length > 0 ? dvpRows.map((r, i) => i === 0 ? { ...r, valueAtClosing: v } : r) : [emptyDvpRow(v, DeferredVariablePaymentType.Earnout)] } })}
                            faceValueAriaLabel="Deferred Variable Payments"
                            faceValueErrorKey="deferredVariablePayments"
                            percent={formatPercent(dvpTotal, total)}
                            considerationForEmployees={dvpRows[0]?.considerationForEmployees}
                            onConsiderationChange={(v) => patchDvpFirstRow({ considerationForEmployees: v, netToSeller: computeNetToSeller(dvpRows[0]?.valueAtClosing, v) })}
                            considerationAriaLabel="Deferred Variable Payments - Consideration for Employees"
                            considerationErrorKey="deferredVariablePaymentsConsiderationForEmployees"
                            netToSeller={computeNetToSeller(dvpRows[0]?.valueAtClosing, dvpRows[0]?.considerationForEmployees)}
                            netToSellerAriaLabel="Deferred Variable Payments - Net to Seller"
                            fairValue={dvpRows[0]?.fairValue}
                            onFairValueChange={(v) => patchDvpFirstRow({ fairValue: v })}
                            fairValueAriaLabel="Deferred Variable Payments - Fair Value"
                            fairValueErrorKey="deferredVariablePaymentsFairValue"
                            explanation={dvpRows[0]?.fairValueExplanation}
                            onExplanationChange={(v) => patchDvpFirstRow({ fairValueExplanation: v })}
                            explanationAriaLabel="Deferred Variable Payments - Fair Value Explanation"
                            commentNode={commentInput(LoiTermFormFieldKeys.deferredVariablePaymentsTotal)}
                        />

                        {dvpRows.some((r) => r.type === DeferredVariablePaymentType.Earnout) && earnoutSum > 0 && (
                            <PriceSummaryTableRow
                                {...rowProps}
                                label="Earnout"
                                indented
                                faceValue={(() => { const r = dvpRows.find((r) => r.type === DeferredVariablePaymentType.Earnout); return r?.valueAtClosing ?? null; })()}
                                onFaceValueChange={(v) => {
                                    const idx = dvpRows.findIndex((r) => r.type === DeferredVariablePaymentType.Earnout);
                                    onSummaryChange({ deferredVariablePayments: { rows: idx >= 0 ? dvpRows.map((r, i) => i === idx ? { ...r, valueAtClosing: v } : r) : [...dvpRows, emptyDvpRow(v, DeferredVariablePaymentType.Earnout)] } });
                                }}
                                faceValueAriaLabel="Earnout"
                                commentNode={commentInput(LoiTermFormFieldKeys.earnout)}
                            />
                        )}

                        {dvpRows.some((r) => r.type === DeferredVariablePaymentType.Synthetic) && (
                            <PriceSummaryTableRow
                                {...rowProps}
                                label="Synthetic JV Equity"
                                indented
                                faceValue={(() => { const r = dvpRows.find((r) => r.type === DeferredVariablePaymentType.Synthetic); return r?.valueAtClosing ?? null; })()}
                                onFaceValueChange={(v) => {
                                    const idx = dvpRows.findIndex((r) => r.type === DeferredVariablePaymentType.Synthetic);
                                    onSummaryChange({ deferredVariablePayments: { rows: idx >= 0 ? dvpRows.map((r, i) => i === idx ? { ...r, valueAtClosing: v } : r) : [...dvpRows, emptyDvpRow(v, DeferredVariablePaymentType.Synthetic)] } });
                                }}
                                faceValueAriaLabel="Synthetic JV Equity"
                                commentNode={commentInput(LoiTermFormFieldKeys.syntheticJvEquity)}
                            />
                        )}

                        <PriceSummaryTableRow
                            {...rowProps}
                            label="JV Equity"
                            faceValue={summary?.jvEquity?.row?.valueAtClosing}
                            onFaceValueChange={(v) => onSummaryChange({ jvEquity: { row: { ...summary?.jvEquity?.row, valueAtClosing: v } } })}
                            faceValueAriaLabel="JV Equity"
                            faceValueErrorKey="jvEquityValueAtClosing"
                            percent={formatPercent(jvVal, total)}
                            considerationForEmployees={summary?.jvEquity?.row?.considerationForEmployees}
                            onConsiderationChange={(v) => onSummaryChange({ jvEquity: { row: { ...summary?.jvEquity?.row, considerationForEmployees: v, netToSeller: computeNetToSeller(summary?.jvEquity?.row?.valueAtClosing ? Number(summary.jvEquity!.row!.valueAtClosing) : null, v) } } })}
                            considerationAriaLabel="JV Equity - Consideration for Employees"
                            considerationErrorKey="jvEquityConsiderationForEmployees"
                            netToSeller={computeNetToSeller(summary?.jvEquity?.row?.valueAtClosing ? Number(summary.jvEquity?.row?.valueAtClosing) : null, summary?.jvEquity?.row?.considerationForEmployees)}
                            netToSellerAriaLabel="JV Equity - Net to Seller"
                            fairValue={summary?.jvEquity?.row?.fairValue}
                            onFairValueChange={(v) => onSummaryChange({ jvEquity: { row: { ...summary?.jvEquity?.row, fairValue: v } } })}
                            fairValueAriaLabel="JV Equity - Fair Value"
                            fairValueErrorKey="jvEquityFairValue"
                            explanation={summary?.jvEquity?.row?.fairValueExplanation}
                            onExplanationChange={(v) => onSummaryChange({ jvEquity: { row: { ...summary?.jvEquity?.row, fairValueExplanation: v } } })}
                            explanationAriaLabel="JV Equity - Fair Value Explanation"
                            commentNode={commentInput(LoiTermFormFieldKeys.jvEquityTotal)}
                        />

                        <PriceSummaryTableRow
                            {...rowProps}
                            label="Corporate Equity"
                            faceValue={summary?.corporateEquity?.row?.valueAtClosing}
                            onFaceValueChange={(v) => onSummaryChange({ corporateEquity: { row: { ...summary?.corporateEquity?.row, valueAtClosing: v } } })}
                            faceValueAriaLabel="Corporate Equity"
                            faceValueErrorKey="corporateEquityValueAtClosing"
                            percent={formatPercent(corpVal, total)}
                            considerationForEmployees={summary?.corporateEquity?.row?.considerationForEmployees}
                            onConsiderationChange={(v) => onSummaryChange({ corporateEquity: { row: { ...summary?.corporateEquity?.row, considerationForEmployees: v, netToSeller: computeNetToSeller(summary?.corporateEquity?.row?.valueAtClosing ? Number(summary.corporateEquity!.row!.valueAtClosing) : null, v) } } })}
                            considerationAriaLabel="Corporate Equity - Consideration for Employees"
                            considerationErrorKey="corporateEquityConsiderationForEmployees"
                            netToSeller={computeNetToSeller(summary?.corporateEquity?.row?.valueAtClosing ? Number(summary.corporateEquity?.row?.valueAtClosing) : null, summary?.corporateEquity?.row?.considerationForEmployees)}
                            netToSellerAriaLabel="Corporate Equity - Net to Seller"
                            fairValue={summary?.corporateEquity?.row?.fairValue}
                            onFairValueChange={(v) => onSummaryChange({ corporateEquity: { row: { ...summary?.corporateEquity?.row, fairValue: v } } })}
                            fairValueAriaLabel="Corporate Equity - Fair Value"
                            fairValueErrorKey="corporateEquityFairValue"
                            explanation={summary?.corporateEquity?.row?.fairValueExplanation}
                            onExplanationChange={(v) => onSummaryChange({ corporateEquity: { row: { ...summary?.corporateEquity?.row, fairValueExplanation: v } } })}
                            explanationAriaLabel="Corporate Equity - Fair Value Explanation"
                            commentNode={commentInput(LoiTermFormFieldKeys.corporateEquityTotal)}
                        />

                        <PriceSummaryTableRow
                            {...rowProps}
                            label="Other"
                            faceValue={summary?.other}
                            onFaceValueChange={(v) => onSummaryChange({ other: v })}
                            faceValueAriaLabel="Other"
                            faceValueErrorKey="other"
                            percent={formatPercent(other, total)}
                            considerationForEmployees={summary?.otherConsiderationForEmployees}
                            onConsiderationChange={(v) => onSummaryChange({ otherConsiderationForEmployees: v, otherNetToSeller: computeNetToSeller(summary?.other ? Number(summary.other) : null, v) })}
                            considerationAriaLabel="Other - Consideration for Employees"
                            considerationErrorKey="otherConsiderationForEmployees"
                            netToSeller={computeNetToSeller(summary?.other ? Number(summary.other) : null, summary?.otherConsiderationForEmployees)}
                            netToSellerAriaLabel="Other - Net to Seller"
                            fairValue={summary?.otherFairValue}
                            onFairValueChange={(v) => onSummaryChange({ otherFairValue: v })}
                            fairValueAriaLabel="Other - Fair Value"
                            fairValueErrorKey="otherFairValue"
                            explanation={summary?.otherFairValueExplanation}
                            onExplanationChange={(v) => onSummaryChange({ otherFairValueExplanation: v })}
                            explanationAriaLabel="Other - Fair Value Explanation"
                            commentNode={commentInput(LoiTermFormFieldKeys.other)}
                        />

                        {total > 0 && (
                            <tr className="terms-form-summary-total">
                                <td className="terms-form-col-item table-label-cell">
                                    Total Face Value, at Closing
                                </td>
                                <td className="terms-form-col-value">
                                    <Input
                                        type="text"
                                        value={Helpers.currencyFormat(total, false)}
                                        disabled
                                        readOnly
                                        className="form-control loi-form-input"
                                        aria-label="Total Face Value at Closing"
                                    />
                                </td>
                                <td className="terms-form-col-percent">
                                    <Input
                                        type="text"
                                        value="100%"
                                        disabled
                                        readOnly
                                        className="form-control loi-form-input terms-form-percent-label"
                                        aria-label="Percent of Total"
                                    />
                                </td>
                                {showEmployeeCols && (
                                    <>
                                        <td className="terms-form-col-value" />
                                        <td className="terms-form-col-value" />
                                    </>
                                )}
                                {showFairValueCols && (
                                    <>
                                        <td className="terms-form-col-value">
                                            <CurrencyInput
                                                name="totalFairValue"
                                                value={totalFairValue > 0 ? String(totalFairValue) : ''}
                                                allowDecimals
                                                prefix="$"
                                                disabled
                                                className="form-control loi-form-input"
                                                aria-label="Total Fair Value"
                                            />
                                        </td>
                                        <td className="terms-form-col-explanation" />
                                    </>
                                )}
                                {commentCell(LoiTermFormFieldKeys.totalPurchasePrice)}
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
